import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';

export const settingsError = (error, fallback) => {
  if (typeof error === 'string') return error;
  if (typeof error?.detail === 'string') return error.detail;
  if (typeof error?.message === 'string') return error.message;
  const details = error?.message || error;
  const messages = Object.entries(details || {}).filter(([key]) => key !== 'status')
    .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(' ') : String(value)}`);
  return messages.join(' ') || fallback;
};

export default function useCredentialSettings({ client, endpoint, fields, enabled = true, onSaved, successMessage }) {
  const [state, setState] = useState({ data: null, values: {}, baseline: {}, clear: {}, loading: enabled, error: '' });
  const [saving, setSaving] = useState(false);
  const lock = useRef(false);
  const sequence = useRef(0);
  const load = useCallback(async () => {
    const request = ++sequence.current;
    setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const response = await client.get(endpoint);
      if (request !== sequence.current) return;
      const data = response?.data?.data || response?.data || {};
      const values = Object.fromEntries(fields.map(({ name, secret, type }) => [name, secret ? '' : data[name] ?? (type === 'checkbox' ? false : '')]));
      setState({ data, values, baseline: values, clear: {}, loading: false, error: '' });
    } catch (error) {
      if (request === sequence.current) setState({ data: null, values: {}, baseline: {}, clear: {}, loading: false, error: settingsError(error, 'Settings could not be loaded.') });
    }
  }, [client, endpoint, fields]);
  useEffect(() => {
    if (enabled) load();
    return () => { sequence.current += 1; };
  }, [enabled, load]);
  const change = (name, value) => setState((current) => ({ ...current, values: { ...current.values, [name]: value } }));
  const clearSecret = (name, checked) => setState((current) => ({ ...current, clear: { ...current.clear, [name]: checked }, values: { ...current.values, [name]: '' } }));
  const payload = Object.fromEntries(fields.filter(({ name, secret }) => secret
    ? state.clear[name] || Boolean(state.values[name])
    : state.values[name] !== state.baseline[name]).map(({ name }) => [name, state.clear[name] ? '' : state.values[name]]));
  const save = async (event) => {
    event.preventDefault();
    if (lock.current || !enabled || state.loading || !state.data || !Object.keys(payload).length) return;
    lock.current = true;
    setSaving(true);
    try {
      await client.patch(endpoint, payload);
      onSaved?.(payload);
      // Discard credentials immediately after confirmed success, including if refetch fails.
      setState((current) => ({ ...current, values: { ...current.values, ...Object.fromEntries(fields.filter((field) => field.secret).map(({ name }) => [name, ''])) }, clear: {} }));
      toast.success(successMessage);
      await load();
    } catch (error) {
      toast.error(settingsError(error, 'Settings could not be saved.'));
    } finally {
      lock.current = false;
      setSaving(false);
    }
  };
  return { ...state, saving, load, change, clearSecret, save, dirty: Boolean(Object.keys(payload).length), disabled: !enabled || state.loading || saving || !state.data };
}
